import { ColourPalette, TriviaList } from '@repo/types'
import { Button, Container, Label } from '@repo/ui'
import { useSuccessButton } from '@repo/ui/client'
import { Field, Form, Formik } from 'formik'
import { observer } from 'mobx-react-lite'
import { useHistory } from 'react-router-dom'

import { AppHeader } from '@/components/AppHeader'
import { AppHeaderLogo } from '@/components/AppHeaderLogo'
import { ColourPaletteField } from '@/components/ColourPaletteSelector'
import { ExternalLink } from '@/components/ExternalLink'
import { BackButton } from '@/components/icon-buttons/IconButtons'
import { Main } from '@/components/Main'
import { Page } from '@/components/Page'
import { PageTitle } from '@/components/PageTitle'
import { Title } from '@/components/Title'
import { ImageUpload } from '@/pages/settings/components/ImageUpload'

interface EditTriviaListDetailsProps {
  baseUrl?: string
  imageStorageFolder: string
  initialValues: TriviaList
  isSaving: boolean
  updateTriviaList: (values: TriviaList) => void
}

export const EditTriviaListDetails = observer(
  ({
    baseUrl = '/',
    imageStorageFolder,
    initialValues,
    isSaving,
    updateTriviaList
  }: EditTriviaListDetailsProps) => {
    const { showSuccess, showSuccessButtonState } = useSuccessButton()
    const history = useHistory()

    const handleSubmit = async (values: TriviaList) => {
      await updateTriviaList(values)
      await showSuccessButtonState()

      history.push(`${baseUrl}trivia/lists/edit/${initialValues.id}`)
    }

    return (
      <Page title='Edit Trivia List Details'>
        <AppHeader
          actionsLeft={<BackButton url={`${baseUrl}trivia/lists/edit/${initialValues.id}`} />}
          header={<AppHeaderLogo />}
        />

        <Main>
          <Container size='medium'>
            <PageTitle className='mb-6 text-center' size='large' disableTruncation>
              Edit Trivia List Details
            </PageTitle>

            <Formik
              initialValues={{
                ...initialValues,
                colour: initialValues.colour || ColourPalette.PURPLE,
                image: { url: initialValues.image?.url || '' },
                title: initialValues.title,
                video: { url: initialValues.video?.url || '' },
                videoPreGame: { url: initialValues.videoPreGame?.url || '' }
              }}
              onSubmit={handleSubmit}
            >
              {({ isSubmitting, setFieldValue, values }) => (
                <Form
                  className='flex flex-col gap-8 '
                  placeholder={undefined}
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                >
                  <div className='flex flex-col gap-2'>
                    <Label>Title</Label>
                    <Field name='title' />
                  </div>
                  <div className='flex flex-col gap-2'>
                    <Label>Colour</Label>
                    <ColourPaletteField />
                  </div>
                  <div className='flex flex-col gap-2'>
                    <Label>Image</Label>
                    <Field name='image.url' type='hidden' />
                    <ImageUpload
                      initialValue={
                        values.image.url ? { ...values.image, filename: '' } : undefined
                      }
                      storageFolder={imageStorageFolder}
                      onRemoveImage={() => {
                        setFieldValue('image.url', '')
                      }}
                      onUploadSuccess={({ url }) => {
                        setFieldValue('image.url', url)
                      }}
                    />
                  </div>

                  <div className='flex flex-col gap-4'>
                    <Title
                      description={
                        <>
                          You can provide Youtube or Vimeo URLs to play on a video screen.{' '}
                          <ExternalLink href='https://go.rockstar.bingo/jumbotron'>
                            Learn more
                          </ExternalLink>
                        </>
                      }
                      heading='Jumbotron Videos'
                    />

                    <div className='flex flex-col gap-6'>
                      <div className='flex flex-col gap-2'>
                        <Label>Default Video</Label>
                        <Field name='video.url' />
                      </div>

                      <div className='flex flex-col gap-2'>
                        <Label>Pre-game Video</Label>
                        <Field name='videoPreGame.url' />
                      </div>
                    </div>
                  </div>
                  <Button
                    isLoading={isSubmitting || isSaving}
                    loadingText='Saving...'
                    showSuccess={showSuccess}
                    successText='Saved'
                    text='Save'
                    type='submit'
                  />
                </Form>
              )}
            </Formik>
          </Container>
        </Main>
      </Page>
    )
  }
)
